import styled from '@emotion/styled'

import { Div } from './Div'
import { Icon, IconName } from './Icon'
import { Link } from './Link'
import { Color, theme } from '../constants'

type FooterNavProps = {
  className?: string
  items: {
    label: string
    path: string
    icon: IconName
    badge?: number
    active?: boolean
  }[]
}

const Nav = styled.nav({
  backgroundColor: Color.WHITE,
  borderTop: theme.border.style.thin,
  position: 'relative',
})

const Ol = styled.ol`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  margin: 0;
`

const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => !['active'].includes(prop),
})((props: { active?: boolean }) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: theme.spacing[1],
  color: props.active ? Color.BLUE : Color.DARK_GREY,
  paddingTop: theme.spacing[2],
  paddingRight: theme.spacing[2],
  paddingBottom: theme.spacing[2],
  paddingLeft: theme.spacing[2],
  cursor: 'pointer',
  textDecoration: 'none',
  fontSize: theme.font.size[6.5],

  '&:hover': {
    color: Color.BLUE,
  },
}))

const Badge = styled.span({
  backgroundColor: Color.RED,
  color: Color.WHITE,
  borderRadius: 999,
  minWidth: '1.2em',
  minHeight: '1.2em',
  lineHeight: '1.2em',
  textAlign: 'center',
  paddingLeft: 4,
  paddingRight: 4,
  position: 'absolute',
  top: -4,
  left: '60%',
})

const Active = styled.div<{ itemCount: number; selectedIndex: number }>(
  ({ itemCount, selectedIndex }) => ({
    position: 'absolute',
    bottom: 0,
    width: `${100 / itemCount}%`,
    height: 3,
    left: 0,
    background: Color.BLUE,
    transform: `translateX(${100 * selectedIndex}%)`,
    transition: `transform ${theme.animation.duration}s ease-in-out`,
    pointerEvents: 'none',
  }),
)

export const FooterNav = ({ className, items }: FooterNavProps) => {
  const selectedIndex = items.findIndex((item) => item.active)

  return (
    <Nav className={className}>
      <Ol>
        {items.map(({ label, path, icon, badge, active }) => (
          <StyledLink to={path} key={path} active={active}>
            <Div styles={{ position: 'relative' }}>
              <Icon
                name={icon}
                size="md-plus"
                design={active ? 'solid' : 'outline'}
              />

              {!!badge && <Badge role="status">{badge}</Badge>}
            </Div>
            {label}
          </StyledLink>
        ))}
      </Ol>
      {selectedIndex === -1 ? null : (
        <Active itemCount={items.length} selectedIndex={selectedIndex} />
      )}
    </Nav>
  )
}
